import { parseDate } from "@internationalized/date"
import { cx } from "@shared/components/untitledui/util/cx"
import moment from "moment"
import {
  Button,
  Calendar,
  CalendarCell,
  CalendarGrid,
  CalendarGridBody,
  CalendarGridHeader,
  CalendarHeaderCell,
  DatePicker,
  Dialog,
  Group,
  Heading,
  Popover,
} from "react-aria-components"
import styles from "../FollowedCourseMenu.module.css"
import { FollowedCourseArrowIcon } from "./FollowedCourseArrowIcon"
import { formatVisibleDateRange, type FollowedCourseDays } from "./followed-course-dates"

export function FollowedCourseDatePicker({
  value,
  visibleRange,
  days,
  onChange,
}: {
  value: string
  /** The first and last visible days, when more than the center one shows. */
  visibleRange: { first: string; last: string } | null
  days: FollowedCourseDays
  onChange: (value: string) => void
}) {
  const visibleDateRange =
    visibleRange === null
      ? moment(value).format("MMMM D, YYYY")
      : formatVisibleDateRange(visibleRange.first, visibleRange.last)

  return (
    <DatePicker
      aria-label="Center lesson date"
      value={parseDate(value)}
      onChange={(date) => {
        if (date !== null) onChange(date.toString())
      }}
      minValue={days.bounds === null ? undefined : parseDate(days.bounds.start)}
      maxValue={days.bounds === null ? undefined : parseDate(days.bounds.end)}
      isDateUnavailable={(date) => !days.isMeetingDay(date.toString())}
      className="mx-auto w-fit"
      data-test="followed course date picker"
    >
      <Group className="flex justify-center">
        <Button
          aria-label={`Choose date, ${moment(value).format("MMMM D, YYYY")}`}
          className={`${styles.datePicker} cursor-pointer rounded outline-hidden hover:text-link-blue focus-visible:outline-2 focus-visible:outline-focus-ring`}
          data-test="followed course date picker button"
        >
          {visibleDateRange}
        </Button>
      </Group>
      <Popover
        placement="bottom"
        offset={8}
        className="z-50 rounded-xl bg-white p-4 shadow-xl ring-1 ring-black/10 outline-hidden"
      >
        <Dialog className="outline-hidden">
          <Calendar className="w-72" data-test="followed course date picker calendar">
            <header className="mb-3 grid grid-cols-[2.25rem_1fr_2.25rem] items-center">
              <CalendarStepButton slot="previous" label="Previous month" direction={-1} />
              <Heading className="text-center text-base font-bold" />
              <CalendarStepButton slot="next" label="Next month" direction={1} />
            </header>
            <CalendarGrid className="w-full border-separate border-spacing-1">
              <CalendarGridHeader>
                {(day) => (
                  <CalendarHeaderCell className="pb-1 text-xs font-semibold text-gray-500">
                    {day}
                  </CalendarHeaderCell>
                )}
              </CalendarGridHeader>
              <CalendarGridBody>
                {(date) => (
                  <CalendarCell
                    date={date}
                    className={({ isDisabled, isFocusVisible, isOutsideMonth, isSelected }) =>
                      cx(
                        "flex size-9 cursor-pointer items-center justify-center rounded-full text-sm outline-hidden",
                        isOutsideMonth && "text-gray-300",
                        isDisabled && "cursor-not-allowed text-gray-300 line-through",
                        !isDisabled && !isSelected && "hover:bg-gray-100",
                        isSelected && "bg-link-blue font-bold text-white",
                        isFocusVisible && "ring-2 ring-focus-ring ring-offset-1",
                      )
                    }
                  />
                )}
              </CalendarGridBody>
            </CalendarGrid>
          </Calendar>
        </Dialog>
      </Popover>
    </DatePicker>
  )
}

function CalendarStepButton({
  slot,
  label,
  direction,
}: {
  slot: "previous" | "next"
  label: string
  direction: 1 | -1
}) {
  return (
    <Button
      slot={slot}
      aria-label={label}
      className="flex size-9 cursor-pointer items-center justify-center rounded-full text-hint outline-hidden hover:bg-gray-100 hover:text-link-blue focus-visible:outline-2 focus-visible:outline-focus-ring"
    >
      <FollowedCourseArrowIcon direction={direction} className="size-6 p-1" />
    </Button>
  )
}
